import { useState, type FormEvent } from 'react';
import { useAuth, type CurrentUser } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';

export function OtpForm({ challengeToken, onSuccess }: {
  challengeToken: string;
  onSuccess: (user: CurrentUser) => void;
}) {
  const { verifyOtp } = useAuth();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      onSuccess(await verifyOtp(challengeToken, code.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-sm rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-6 shadow-sm flex flex-col gap-4"
      noValidate
    >
      <header className="mb-2">
        <h1 className="text-xl font-semibold">Two-factor authentication</h1>
        <p className="text-sm text-[var(--co-muted)]">
          {useRecovery ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </header>
      <FormField label={useRecovery ? 'Recovery code' : 'Authenticator code'} htmlFor="otp-code" error={error ?? undefined}>
        <Input id="otp-code" autoComplete="one-time-code" inputMode={useRecovery ? 'text' : 'numeric'}
               required value={code} onChange={(e) => setCode(e.target.value)} />
      </FormField>
      <Button type="submit" disabled={pending}>
        {pending ? 'Verifying…' : 'Verify'}
      </Button>
      <button type="button" className="text-sm text-[var(--co-muted)] underline"
              onClick={() => setUseRecovery((v) => !v)}>
        {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
      </button>
    </form>
  );
}
